/**
 * @privisy/domain: the pure business rules every workspace shares (#1692).
 *
 * One home for pricing and credit defaults, tier ranking, the grade ladder,
 * the severity-cap ladder and the tracking-consent rule, so the backend, the
 * dashboard, www and ui-bridge can import them instead of keeping hand-synced
 * copies pinned by parity tests.
 *
 * Constraints (enforced by purity.test.ts and eslint.config.mjs):
 * - No imports of any kind. The backend loads this file as raw TypeScript
 *   through Node's type stripping, whose CJS resolver cannot follow an
 *   extensionless relative import, and client bundles must never reach `pg`
 *   or a Node built-in through it.
 * - Erasable syntax only: no `enum`, `namespace` or parameter properties.
 * - No `process`, `window`, `document` or other runtime globals. Env overrides
 *   stay with the caller (see `parsePositiveInt`).
 */

// ---------------------------------------------------------------------------
// Grade ladder
// ---------------------------------------------------------------------------

export type Grade = 'A' | 'B' | 'C' | 'D' | 'F';

/**
 * Score bands, highest first. A score below every `min` is an F. The SQL
 * `CASE` in packages/backend/src/models/experiment-funnel.ts cannot import
 * this, so experiment-funnel.test.ts pins its boundaries against
 * `getLetterGrade`.
 */
export const GRADE_THRESHOLDS: ReadonlyArray<{ grade: Exclude<Grade, 'F'>; min: number }> = [
  { grade: 'A', min: 90 },
  { grade: 'B', min: 80 },
  { grade: 'C', min: 70 },
  { grade: 'D', min: 60 },
];

/**
 * The one score→letter ladder for every surface (issue #722): a score can
 * never display one grade and travel as another.
 */
export function getLetterGrade(score: number): Grade {
  for (const { grade, min } of GRADE_THRESHOLDS) {
    if (score >= min) return grade;
  }
  return 'F';
}

// ---------------------------------------------------------------------------
// Severity-cap ladder
// ---------------------------------------------------------------------------

export type CapSeverity = 'critical' | 'high' | 'medium' | 'low' | 'warning';

/**
 * How far a finding bounds the score, tightest first (#1119, #1160, #1237).
 * A non-compliant check caps by its severity; `warning` is the soft 99 rung
 * for an unscored, non-`advisory` warning.
 */
export const SEVERITY_CAP_LADDER: ReadonlyArray<{ severity: CapSeverity; cap: number }> = [
  { severity: 'critical', cap: 49 },
  { severity: 'high', cap: 74 },
  { severity: 'medium', cap: 89 },
  { severity: 'low', cap: 94 },
  { severity: 'warning', cap: 99 },
];

/** The check fields the cap reads, declared structurally so this file imports nothing. */
export interface CapCheck {
  status: string;
  severity?: string;
  isBinaryCheck?: boolean;
  advisory?: boolean;
}

function triggersRung(check: CapCheck, severity: CapSeverity): boolean {
  if (severity === 'warning') {
    return !check.isBinaryCheck && check.status === 'warning' && !check.advisory;
  }
  return check.status === 'non_compliant' && check.severity === severity;
}

/**
 * The tightest cap any check triggers, or null.
 *
 * A non-binary failure carries no point value, so the binary-only percentage
 * cannot see it; the ladder keeps the score off 100 while a visible failure
 * exists, without an uncapped additive stack (#1119, #1160). The soft 99
 * warning rung extends that to unscored warnings, such as cannot-assess or
 * inconclusive findings, which would otherwise read 100 next to a 'warning'
 * status (#1237).
 *
 * What never caps: binary-check warnings (their points already count),
 * `advisory` warnings (#1132: a satisfied waiver, not a compliance gap), and
 * `info` failures. The full history lives on `severityCapFor` in
 * packages/backend/src/utils/helpers.ts.
 */
export function severityCap(checks: ReadonlyArray<CapCheck>): { severity: CapSeverity; cap: number } | null {
  for (const rung of SEVERITY_CAP_LADDER) {
    if (checks.some((c) => triggersRung(c, rung.severity))) return { severity: rung.severity, cap: rung.cap };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Credits, pricing and tiers
// ---------------------------------------------------------------------------

/**
 * Free credits granted at registration: none since the locked-free-audit
 * cutover (#1763, epic #1767). A code constant, not env-configurable, so a
 * stale hosting override (e.g. FREE_CREDITS=50) cannot change the access
 * model (#353). Accounts created before the cutover keep the credit they got.
 */
export const FREE_CREDITS = 0;

/**
 * Pack defaults (#1311): one $49 purchase is one credit, one credit is one
 * full audit. The backend lets CREDITS_PER_PURCHASE / CREDIT_PACK_PRICE_CENTS
 * override these to match the Stripe Payment Link; www displays the defaults.
 */
export const DEFAULT_CREDITS_PER_PURCHASE = 1;
export const DEFAULT_CREDIT_PACK_PRICE_CENTS = 4900;

/**
 * Continuous Monitoring (#1313, #1315). Stripe bills it from
 * STRIPE_MONITOR_ANNUAL_PRICE_ID, so this figure has to match that Price by hand.
 */
export const MONITOR_ANNUAL_PRICE_CENTS = 34800;

/**
 * Privisy Shield, as offered by the fake-door test on reports
 * (docs/economics/decisions.md, 2026-10-21). Shield is not built and nothing
 * is charged: this is the price the offer quotes, monitoring included, so the
 * test measures interest at a real number.
 */
export const SHIELD_PREVIEW_MONTHLY_PRICE_CENTS = 4900;

// ---------------------------------------------------------------------------
// CCPA CPI-adjusted figures
// ---------------------------------------------------------------------------

/**
 * Scope and penalty figures the CPPA adjusts for inflation every two years
 * under Civ. Code § 1798.199.95(d), effective January 1, 2025 and holding
 * through 2026. The statute text still prints the old round numbers ($2,500,
 * $7,500, $25,000,000, $100 to $750); quote these instead. They live here, not
 * in www/src/lib/regulations, because the dashboard quotes them too and cannot
 * import from www. ccpa-cpi.test.ts fails on `nextAdjustment`, so the update
 * cannot be forgotten.
 * https://cppa.ca.gov/regulations/cpi_adjustment.html
 */
export const CCPA_CPI_FIGURES = {
  revenueThreshold: '$26,625,000',
  finePerViolation: '$2,663',
  finePerIntentionalViolation: '$7,988',
  /** § 1798.150(a)(1)(A) statutory damages, the private right of action. */
  statutoryDamagesRange: '$107 to $799',
  effectiveFrom: '2025-01-01',
  nextAdjustment: '2027-01-01',
} as const;

/**
 * Parse a positive integer, falling back when the input is unset OR
 * malformed. A bare `parseInt` returns NaN for a typo'd value, and NaN
 * serialises to JSON `null`, which passes every `!== undefined` guard on the
 * client and renders "Buy null full audits — $0" (#1398 review).
 */
export function parsePositiveInt(raw: string | undefined, fallback: number): number {
  const n = parseInt(raw ?? '', 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * `monitor`/`agency` are the recurring-plan tiers. PRIVISY_PRO_USER_IDS
 * members and admins also resolve to `agency` (the top tier, per #1316) with
 * `unlimited: true`, so a scheduler exemption, plan count or revenue metric
 * must key on a subscription row or on `unlimited`, never on
 * `tier === 'agency'` alone.
 */
export type Tier = 'free' | 'scan' | 'monitor' | 'agency';

const TIER_RANK: Record<Tier, number> = { free: 0, scan: 1, monitor: 2, agency: 3 };

/** True when `tier` entitles at least what `minimum` does (#1318). */
export function tierAtLeast(tier: Tier, minimum: Tier): boolean {
  return TIER_RANK[tier] >= TIER_RANK[minimum];
}

/**
 * How many domains each tier may monitor. `agency: 1` is a placeholder until
 * #1324 decides the agency allowance; PRIVISY_PRO_USER_IDS members and admins
 * resolve to `agency` too, so they share it.
 */
export const MONITOR_DOMAIN_LIMIT: Readonly<Record<Tier, number>> = { free: 0, scan: 0, monitor: 1, agency: 1 };

// ---------------------------------------------------------------------------
// Tracking consent
// ---------------------------------------------------------------------------

/**
 * Tracking-consent cookie: the single opt-out signal for marketing/ad
 * trackers, shared across www and the scanner app via cross-subdomain cookie
 * semantics. GPC is evaluated at read time, never baked into the cookie.
 */
export const TRACKING_CONSENT_COOKIE = 'privisy_consent';

/**
 * The pure consent-decision rule (#780): given the raw tracking-consent
 * cookie value and whether the visitor's browser is signalling Global
 * Privacy Control, decides whether trackers may run.
 *
 * Consent is **opt-out**: Privisy's sites operate under CCPA/CPRA, which
 * permits trackers by default so long as a clear opt-out mechanism exists
 * (/your-privacy-choices) and GPC is honored. Only an explicit
 * `privisy_consent=false` closes the gate — a missing/null/undefined cookie
 * value means the visitor has made no choice, and tracking runs. GPC always
 * takes precedence as an opt-out, regardless of the cookie.
 *
 * Adapters: ui-bridge's hasConsent() (document.cookie + navigator), www's
 * server-rendered layout.tsx (cookie + `sec-gpc` via next/headers) and the
 * backend's isOptOutRequest() (Cookie + Sec-GPC request headers). The inline
 * pre-bundle loader in packages/frontend/index.html runs before any module
 * system exists and hand-mirrors this rule; see its comment.
 */
export function isTrackingAllowed(consentCookieValue: string | null | undefined, gpcActive: boolean): boolean {
  if (gpcActive) return false; // GPC takes precedence as opt-out
  return consentCookieValue !== 'false';
}
